import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {throttle} from 'lodash';
import getDisplayName from '../../utils/getDisplayName';
import {
  componentIsInViewport,
  loadScriptsInHeadSequentially
} from '../../utils/dom';
import { isGetEmbed } from '../../utils/widgetInspector';

/*
 * HOC that encapsulates behavior of grabbing <script> tags and running or
 * loading them. Used by HTML Sections. Works with inline scripts and external
 * ones using the 'src' attribute.
 *
 * Scripts will load as soon as the user scrolls to the component.
 * (GetEmbed scripts are an exception -- they will load ASAP.)
 *
 * Inline scripts will be reloaded every time the HTML section mounts or updates.
 * External scripts will only be loaded once!
 *
 * We need this HOC because inserting HTML content into the DOM does not
 * execute <script> tags. More info here: https://ghinda.net/article/script-tags/
 */

const THROTTLE_WAIT = 500;
export default (WrappedComponent) =>
  class extends Component {
    static displayName =
      `WithScriptTagExecution(${getDisplayName(WrappedComponent)})`;

    static propTypes = {
      content: PropTypes.shape({
        resource: PropTypes.string.isRequired
      }).isRequired
    };

    componentDidMount() {
      this.scrollScripts = [];
      window.addEventListener('scroll', this.handleScroll);
      this.onMountOrUpdate();
    }

    shouldComponentUpdate(...args) {
      const { shouldComponentUpdate } = WrappedComponent.prototype;
      if (!shouldComponentUpdate) {
        return true;
      }
      return shouldComponentUpdate.apply(this, args);
    }

    /*
     * When one HTML Section turns into another (for example,
     * when there are two HTML slides next to each other in a
     * slideshow), the HTMLSection component stays mounted and
     * triggers neither mount nor unmount hooks. We should account
     * for that case here.
     */
    componentDidUpdate() {
      // In case the event listener was removed, attach it back.
      window.removeEventListener('scroll', this.handleScroll);
      window.addEventListener('scroll', this.handleScroll);
      this.onMountOrUpdate();
    }

    componentWillUnmount() {
      window.removeEventListener('scroll', this.handleScroll);
    }

    /*
     * Every time the section changes, we need to make sure
     * that any new scripts will be loaded on scroll.
     */
    onMountOrUpdate() {
      /*
       * We're calculating the scripts to load on mount/update instead of inside
       * `this.handleScroll` itself because `this.getScripts()` is kind of
       * expensive.
       */
      this.scrollScripts = this.getScripts();
      this.handleScroll();
    }

    handleScroll = throttle(() => {
      const shouldLoadScripts = (
        componentIsInViewport(this._node, window)
        || isGetEmbed(this.props.content.resource)
      );
      if (shouldLoadScripts) {
        loadScriptsInHeadSequentially(this.scrollScripts);
        window.removeEventListener('scroll', this.handleScroll);
      }
    }, THROTTLE_WAIT);

    getScripts() {
      const dummy = document.createElement('div');

      dummy.innerHTML = this.props.content.resource;
      /*
       * For HTMLSections, this.props.content.resource is where
       * <script> tags are located.
       *
       * In case we generalize this component and make it usable
       * by things other than HTMLSection, we can make the path
       * to the scripts a configurable option of this HOC.
       *
       * `git blame` this comment to see a prior version of this
       * HOC where that was the case.
       */
      const scripts = dummy.getElementsByTagName('script');
      return scripts ? Array.from(scripts) : [];
    }

    render() {
      return (
        <div ref={(node) => this._node = node}>
          <WrappedComponent {...this.props} />
        </div>
      );
    }
  };
